/**
 * One run of a published pipeline: what happened to each document.
 *
 * Read from the run's load reports (ADR-0034): source file, the Veeva document
 * it became (linked when the connection's vault URL is known), its type, the
 * outcome and — for a failure — a reason in plain language. CSV export is
 * audited server-side.
 */

import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useEffectiveRoles } from "@/features/auth/RolePreviewContext";
import { useMe } from "@/features/users/useMe";

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconDownload, IconExternalLink } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { formatUtc } from "@/features/solutions/shared/runMeta";
import { downloadFile } from "@/shared/api/integration-browse";
import {
  getResultsRun,
  resultsRunCsvPath,
  type ResultsRunDetailOut,
} from "@/shared/api/solutionPages";
import { lifecycleColor, statusColor } from "@/shared/ui/palette";
import { toastApiError } from "@/shared/ui/toast";
import {
  OUTCOME_COLOR,
  OUTCOME_LABEL,
  formatCount,
  formatDuration,
  runResult,
} from "./resultsMeta";

interface Props {
  solutionPage: string;
  runId: string;
  /** Called once the run's details have rendered (e.g. to scroll to them). */
  onLoaded?: () => void;
}

export function RunResultDetail({ solutionPage, runId, onLoaded }: Props) {
  const getToken = useAccessToken();
  // The pipeline page itself is Developer+; a business user sees results only.
  const { data: me } = useMe();
  const roles = useEffectiveRoles(me?.roles ?? []);
  const canOpenPipeline = roles.includes("developer") || roles.includes("administrator");
  const [detail, setDetail] = useState<ResultsRunDetailOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    void (async () => {
      try {
        const res = await getResultsRun(await getToken(), solutionPage, runId);
        if (!cancelled) setDetail(res);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load the run.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, solutionPage, runId]);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  useEffect(() => {
    if (detail || error) onLoadedRef.current?.();
  }, [detail, error]);

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadFile(
        await getToken(),
        resultsRunCsvPath(solutionPage, runId),
        `run-${runId}-documents.csv`,
      );
    } catch (err) {
      toastApiError("Could not export the documents", err);
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <Alert color="red" title="Could not load the run">
        {error}
      </Alert>
    );
  }
  if (!detail) return <Loader size="sm" aria-label="Loading run" />;

  const { run, documents } = detail;
  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={4}>{run.pipeline_name ?? "Run"}</Title>
            <Text size="sm" c="dimmed">
              Started {formatUtc(run.started_at_utc)} · {formatDuration(run.duration_seconds)} ·{" "}
              {formatCount(run.uploaded)} loaded, {formatCount(run.failed)} failed
            </Text>
          </div>
          <Group gap="xs">
            {run.project_id && canOpenPipeline && (
              <Button
                component={RouterLink}
                to={`/projects/${run.project_id}/pipelines/${run.pipeline_id}?tab=execution&run=${run.run_id}`}
                size="xs"
                variant="light"
                leftSection={<IconExternalLink size={14} />}
                data-testid="open-execution"
              >
                Open execution details
              </Button>
            )}
            <Badge color={runResult(run).color || statusColor(run.status)} variant="light">
              {runResult(run).label}
            </Badge>
            {run.lifecycle_state && (
              <Badge color={lifecycleColor(run.lifecycle_state)} variant="outline" tt="capitalize">
                {run.lifecycle_state}
              </Badge>
            )}
            <Button
              size="xs"
              variant="default"
              leftSection={<IconDownload size={14} />}
              onClick={() => void onExport()}
              loading={exporting}
              disabled={documents.length === 0}
            >
              Export CSV
            </Button>
          </Group>
        </Group>

        {run.lifecycle_state && run.lifecycle_state !== "production" && (
          <Text size="xs" c="dimmed">
            This run ran in {run.lifecycle_state}, not Production.
          </Text>
        )}
        {run.status === "failed" && run.error && (
          <Alert color="red" title="The run failed">
            {run.error}
          </Alert>
        )}
        {detail.loads_without_rows > 0 && (
          <Text size="xs" c="dimmed">
            {detail.loads_without_rows === 1 ? "One load" : `${detail.loads_without_rows} loads`} in
            this run recorded counts only — they ran before per-document results were kept.
          </Text>
        )}

        {documents.length === 0 ? (
          <Text size="sm" c="dimmed">
            This run did not load any documents.
          </Text>
        ) : (
          <ScrollArea type="auto">
            <Table striped highlightOnHover withTableBorder fz="sm" miw={760}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Source file</Table.Th>
                  <Table.Th>Veeva document</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Outcome</Table.Th>
                  <Table.Th>Reason</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {documents.map((d, i) => (
                  <Table.Tr key={`${d.object_name}-${d.row}-${i}`}>
                    <Table.Td>{d.row}</Table.Td>
                    <Table.Td>{d.source_file ?? "—"}</Table.Td>
                    <Table.Td>
                      {d.target_id && d.target_url ? (
                        <Anchor href={d.target_url} target="_blank" rel="noreferrer" size="sm">
                          <Group gap={4} wrap="nowrap">
                            {d.target_id}
                            <IconExternalLink size={12} />
                          </Group>
                        </Anchor>
                      ) : (
                        (d.target_id ?? "—")
                      )}
                    </Table.Td>
                    <Table.Td>{d.document_type ?? "—"}</Table.Td>
                    <Table.Td>
                      <Badge color={OUTCOME_COLOR[d.outcome] ?? "gray"} variant="light">
                        {OUTCOME_LABEL[d.outcome] ?? d.outcome}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{d.reason ?? ""}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Stack>
    </Paper>
  );
}
